/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { Checkbox } from "@mui/material";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DetailRow from "../components/DetailRow";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { getAlarmLedColor } from "../utils";

const SpsuDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const isDisconnected = statusLabel === "Disconnected";

  const normalizeAttrKey = (value: string) => value.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  const getAttrValueInsensitive = (key: string) => {
    const direct = parsedAttributes[key];
    if (direct !== undefined) return direct;

    const lowerKey = key.toLowerCase();
    const matchedKey = Object.keys(parsedAttributes).find((k) => k.toLowerCase() === lowerKey);
    if (matchedKey) return parsedAttributes[matchedKey];

    const normalizedKey = normalizeAttrKey(key);
    const matchedNormalizedKey = Object.keys(parsedAttributes).find(
      (k) => normalizeAttrKey(k) === normalizedKey
    );
    return matchedNormalizedKey ? parsedAttributes[matchedNormalizedKey] : undefined;
  };

  const getSpsuValue = (suffix: string) => {
    const spsu = getAttrValueInsensitive(`spsu${suffix}`);
    if (spsu !== undefined) return spsu;

    const sPsu = getAttrValueInsensitive(`sPsu${suffix}`);
    if (sPsu !== undefined) return sPsu;

    return getAttrValueInsensitive(`psu${suffix}`);
  };

  const getArrayAttr = (keys: string[]) => {
    for (const key of keys) {
      const value = getAttrValueInsensitive(key);
      if (Array.isArray(value)) {
        return value;
      }
    }

    return [] as any[];
  };

  const spsuEquipmentModel = getSpsuValue("EquipmentModel") || device.model;
  const spsuEquipmentSn = getSpsuValue("EquipmentSN") || device.serialNumber;
  const spsuSiteInfo = getSpsuValue("SiteInfo");
  const spsuTemperature = getSpsuValue("Temperature");
  const spsuFirmwareVersion = getSpsuValue("FirmwareVersion");
  const spsuFanSpeed = getSpsuValue("FanSpeed");
  const spsuInputVoltage = getSpsuValue("InputVoltage");
  const spsuOutputVoltage = getSpsuValue("OutputVoltage");
  const spsuPowerConsumption = getSpsuValue("PowerConsumption");

  const hasDeviceInfo =
    spsuEquipmentModel ||
    spsuEquipmentSn ||
    spsuSiteInfo ||
    spsuTemperature !== undefined ||
    spsuFirmwareVersion ||
    spsuFanSpeed !== undefined ||
    spsuInputVoltage !== undefined ||
    spsuOutputVoltage !== undefined ||
    spsuPowerConsumption !== undefined;

  const spsuAlarms = getSpsuValue("Alarms");
  const spsuAlarmsMask = getSpsuValue("AlarmsMask");
  const hasSpsuAlarms = spsuAlarms !== undefined || spsuAlarmsMask !== undefined;

  const spsuFanAlarm = getArrayAttr([
    "spsuFanAlram",
    "spsuFanAlarm",
    "sPsuFanAlram",
    "sPsuFanAlarm",
    "psuFanAlram",
    "psuFanAlarm",
  ]);

  const spsuPowerAlarm = getArrayAttr([
    "spsuPowerAlram",
    "spsuPowerAlarm",
    "sPsuPowerAlram",
    "sPsuPowerAlarm",
    "psuPowerAlram",
    "psuPowerAlarm",
  ]);

  const sortByNumber = (items: any[], key: string) =>
    [...items].sort((a, b) => Number(a?.[key]) - Number(b?.[key]));

  const spsuFanAlarmSorted = sortByNumber(spsuFanAlarm, "psuFanNumber");
  const spsuPowerAlarmSorted = sortByNumber(spsuPowerAlarm, "psuChannelNumber");

  const hasSpsuFanAlarm = spsuFanAlarmSorted.length > 0;
  const hasSpsuPowerAlarm = spsuPowerAlarmSorted.length > 0;

  const spsuAlarmDefinitions: AlarmDefinition[] = [
    { bit: 1, name: "Overall Alarm" },
    { bit: 2, name: "Output Over Voltage Alarm" },
    { bit: 3, name: "Input Under Voltage Alarm" },
    { bit: 4, name: "Over Temperature Alarm" },
    { bit: 5, name: "Version Mismatch Alarm" },
  ];

  const renderStatusAlarmCard = (
    title: string,
    items: any[],
    numberKey: string,
    alarmKey: string,
    maskKey: string,
    labelPrefix: string
  ) => {
    if (items.length === 0) return null;

    return (
      <Card className="mb-2 overflow-hidden">
        <CardHeader className="pb-3 px-4 pt-4">
          <CardTitle className="text-sm">{title}</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {items.map((item: any, index: number) => {
              const itemNumber = item?.[numberKey] ?? index + 1;
              const isAlarmActive = Number(item?.[alarmKey]) > 0;
              const isMaskEnabled = Number(item?.[maskKey]) > 0;

              return (
                <div
                  key={`${title}-${itemNumber}-${index}`}
                  className="rounded-xl border p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">
                      {labelPrefix} {itemNumber}
                    </div>

                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive),
                      }}
                    />
                  </div>

                  <div className="mt-3 text-xs flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">
                      Status:{" "}
                      <span className="font-medium text-foreground">
                        {isAlarmActive ? "Alarm Active" : "Normal"}
                      </span>
                    </span>
                    <Checkbox
                      checked={isMaskEnabled}
                      disabled
                      size="small"
                      sx={{
                        padding: 0,
                        transform: "scale(0.7)",
                        color: "var(--foreground)",
                        "&.Mui-disabled": {
                          color: "var(--muted-foreground)",
                        },
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (alarmsOnly) {
    if (!hasSpsuAlarms && !hasSpsuFanAlarm && !hasSpsuPowerAlarm) return null;

    return (
      <>
        {hasSpsuAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">SPSU Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarms={spsuAlarms}
                alarmsMask={spsuAlarmsMask}
                alarmDefinitions={spsuAlarmDefinitions}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>
        )}

        {renderStatusAlarmCard(
          "SPSU Fan Alarm",
          spsuFanAlarmSorted,
          "psuFanNumber",
          "psuFanAlarm",
          "psuFanAlarmMask",
          "Fan"
        )}

        {renderStatusAlarmCard(
          "SPSU Power Alarm",
          spsuPowerAlarmSorted,
          "psuChannelNumber",
          "psuOverPowerConsumptionAlarm",
          "psuOverPowerConsumptionAlarmMask",
          "Channel"
        )}
      </>
    );
  }

  if (!hasDeviceInfo && !hasSpsuAlarms && !hasSpsuFanAlarm && !hasSpsuPowerAlarm) return null;

  return (
    <>
      {hasDeviceInfo && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Device Information</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {spsuSiteInfo && <DetailRow label="Site" value={spsuSiteInfo} />}
            {spsuEquipmentModel && <DetailRow label="Model" value={spsuEquipmentModel} />}
            {spsuEquipmentSn && <DetailRow label="Serial Number" value={spsuEquipmentSn} />}
            {spsuTemperature !== undefined && <DetailRow label="Temperature" value={`${spsuTemperature}°C`} />}
            {spsuFirmwareVersion && <DetailRow label="Firmware Version" value={spsuFirmwareVersion} />}
            {spsuFanSpeed !== undefined && <DetailRow label="Fan Speed" value={`${spsuFanSpeed}`} />}
            {spsuInputVoltage !== undefined && <DetailRow label="Input Voltage" value={`${spsuInputVoltage}`} />}
            {spsuOutputVoltage !== undefined && <DetailRow label="Output Voltage" value={`${spsuOutputVoltage}`} />}
            {spsuPowerConsumption !== undefined && <DetailRow label="Power Consumption" value={`${spsuPowerConsumption}`} />}
          </CardContent>
        </Card>
      )}

      {hasSpsuAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">SPSU Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={spsuAlarms}
              alarmsMask={spsuAlarmsMask}
              alarmDefinitions={spsuAlarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}

      {renderStatusAlarmCard(
        "SPSU Fan Alarm",
        spsuFanAlarmSorted,
        "psuFanNumber",
        "psuFanAlarm",
        "psuFanAlarmMask",
        "Fan"
      )}

      {renderStatusAlarmCard(
        "SPSU Power Alarm",
        spsuPowerAlarmSorted,
        "psuChannelNumber",
        "psuOverPowerConsumptionAlarm",
        "psuOverPowerConsumptionAlarmMask",
        "Channel"
      )}

    </>
  );
};

export default SpsuDeviceDetails;
